import { PALETTE, css, mix } from "./palette";

// Each field's avatar, drawn once into a canvas and handed to the GPU as one
// layer of a texture array. Emoji are font glyphs, so this is the only way to
// get one onto a 3D surface — and drawing the plate here rather than modelling
// it means the wheel carries 22 flat quads instead of 22 more meshes.

export const CHIP_TEXTURE_SIZE = 192;

export interface ChipFace {
  emoji: string;
  kind: "member" | "beer";
  /** Used to letter the plate if the device has no emoji for this face. */
  label?: string;
}

const EMOJI_FONT =
  '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", "Twemoji Mozilla", sans-serif';

interface Ink {
  /** How far to move the glyph to put the middle of its ink on a given point. */
  dx: number;
  dy: number;
  /** False when the glyph painted nothing at all. */
  painted: boolean;
}

const inkCache = new Map<string, Ink>();

/**
 * Where a glyph's ink actually lands, found by drawing it on its own and
 * looking at the pixels.
 *
 * This is deliberately not `measureText`. The plate needs the middle of the
 * *picture* on the middle of the circle, and emoji sit differently in every
 * emoji font, so some correction is needed — but TextMetrics is the wrong
 * place to get it. Safari returns nothing usable for colour emoji, and the
 * values it does return arrive as NaN rather than as undefined, so a guard
 * that asks whether they are numbers says yes: the offsets come out NaN,
 * fillText silently draws nothing, and every plate on the wheel ends up an
 * empty circle. Counting pixels asks the only question that matters, and
 * gives the same answer on every engine.
 */
function measureInk(emoji: string, size: number, font: string): Ink {
  const key = `${emoji}@${size}`;
  const cached = inkCache.get(key);
  if (cached) return cached;

  const blank: Ink = { dx: 0, dy: 0, painted: false };
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return blank;

  ctx.font = font;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#000000";
  ctx.fillText(emoji, size / 2, size / 2);

  let pixels: Uint8ClampedArray;
  try {
    pixels = ctx.getImageData(0, 0, size, size).data;
  } catch {
    return blank;
  }

  let minX = size;
  let minY = size;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (pixels[(y * size + x) * 4 + 3] > 8) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }

  const ink: Ink =
    maxX < 0
      ? blank
      : { dx: size / 2 - (minX + maxX) / 2, dy: size / 2 - (minY + maxY) / 2, painted: true };
  inkCache.set(key, ink);
  return ink;
}

/** Two letters to fall back on when a device has no glyph for the emoji. */
export function initials(label: string | undefined): string {
  const words = (label ?? "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "?";
  return words.length === 1
    ? words[0].slice(0, 2).toUpperCase()
    : (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * Draws one avatar plate, centred, filling a square of `size` px.
 *
 * Flat, like the painted number plate on a real wheel's pocket floor —
 * no drop shadow and no bevel, because the plate lies *in* the pocket rather
 * than standing up in it, and shading that implies a raised object reads as a
 * mistake once it is lying down.
 */
export function drawChipFace(ctx: CanvasRenderingContext2D, size: number, face: ChipFace) {
  const centre = size / 2;
  const radius = size * 0.44;
  const beer = face.kind === "beer";

  ctx.clearRect(0, 0, size, size);

  const face1 = beer ? PALETTE.gold : PALETTE.bone;
  const face2 = beer ? PALETTE.goldDeep : PALETTE.paper2;
  const gradient = ctx.createLinearGradient(0, centre - radius, 0, centre + radius);
  gradient.addColorStop(0, css(mix(face1, PALETTE.bone, 0.3)));
  gradient.addColorStop(1, css(face2));
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(centre, centre, radius, 0, Math.PI * 2);
  ctx.fill();

  // The plate's edge, dark enough to hold the shape against a pale pocket.
  ctx.lineWidth = size * 0.035;
  ctx.strokeStyle = css(beer ? PALETTE.brassDeep : PALETTE.ink, 0.8);
  ctx.beginPath();
  ctx.arc(centre, centre, radius - ctx.lineWidth / 2, 0, Math.PI * 2);
  ctx.stroke();

  const font = `${Math.round(size * 0.52)}px ${EMOJI_FONT}`;
  const ink = measureInk(face.emoji, size, font);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  // Set explicitly: the plate's gradient is still the fill style otherwise,
  // and a device that draws the emoji as an ordinary glyph rather than a
  // colour one would paint it in almost exactly the colour of the plate.
  ctx.fillStyle = css(PALETTE.ink);

  if (ink.painted) {
    ctx.font = font;
    ctx.fillText(face.emoji, centre + ink.dx, centre + ink.dy);
  } else {
    // No emoji font, or a glyph this device doesn't have. A plate with a name
    // on it is still a plate you can tell from the other 21.
    ctx.font = `600 ${Math.round(size * 0.3)}px "JetBrains Mono", ui-monospace, monospace`;
    ctx.fillText(initials(face.label), centre, centre);
  }
}

export function createChipCanvas(face: ChipFace, size = CHIP_TEXTURE_SIZE): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (ctx) drawChipFace(ctx, size, face);
  return canvas;
}

/**
 * All the faces as one array texture — layer index = face index, which is what
 * the chip instances carry.
 *
 * Mip levels are filled by redrawing each face smaller rather than being
 * generated on the GPU: the chips are small on screen and a texture with no
 * mips crawls with aliasing as the wheel turns, and a canvas downscale is both
 * good quality and about ten lines instead of a whole compute pass.
 */
export function createChipTextureArray(
  device: GPUDevice,
  faces: ChipFace[],
  size = CHIP_TEXTURE_SIZE,
): GPUTexture {
  const mipLevelCount = Math.floor(Math.log2(size)) + 1;
  const texture = device.createTexture({
    label: "roulette-chips",
    size: [size, size, Math.max(1, faces.length)],
    format: "rgba8unorm-srgb",
    mipLevelCount,
    usage:
      GPUTextureUsage.TEXTURE_BINDING |
      GPUTextureUsage.COPY_DST |
      GPUTextureUsage.RENDER_ATTACHMENT,
  });

  const scratch = document.createElement("canvas");
  const scratchCtx = scratch.getContext("2d");

  faces.forEach((face, layer) => {
    const source = createChipCanvas(face, size);
    for (let level = 0; level < mipLevelCount; level++) {
      const levelSize = Math.max(1, size >> level);
      let image: HTMLCanvasElement = source;
      if (level > 0 && scratchCtx) {
        scratch.width = levelSize;
        scratch.height = levelSize;
        scratchCtx.clearRect(0, 0, levelSize, levelSize);
        scratchCtx.drawImage(source, 0, 0, levelSize, levelSize);
        image = scratch;
      }
      device.queue.copyExternalImageToTexture(
        { source: image },
        { texture, mipLevel: level, origin: [0, 0, layer] },
        [levelSize, levelSize, 1],
      );
    }
  });

  return texture;
}
